import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import SearchPage from "./pages/SearchPage";
import SpecialtyWilaya from "./pages/SpecialtyWilaya";
import DoctorProfile from "./pages/DoctorProfile";
import PatientDashboard from "./pages/PatientDashboard";
import DoctorDashboard from "./pages/DoctorDashboard";
import DoctorRegister from "./pages/DoctorRegister";
import AdminDashboard from "./pages/AdminDashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Settings from "./pages/Settings";
import About from "./pages/About";
import Favorites from "./pages/Favorites";
import Contact from "./pages/Contact";
import Partenariat from './pages/Partenariat';
import PharmacyLanding from './pages/PharmacyLanding';
import HealthToolsHub from './pages/tools/HealthToolsHub';
import PediatricDosage from './pages/tools/PediatricDosage';
import BmiCalculator from './pages/tools/BmiCalculator';
import OvulationCalculator from './pages/tools/OvulationCalculator';
import PregnancyTracker from './pages/tools/PregnancyTracker';
import VaccinationCalendar from './pages/tools/VaccinationCalendar';
import CardiovascularRisk from './pages/tools/CardiovascularRisk';
import EmergencyTriage from './pages/tools/EmergencyTriage';
import GlycemiaConverter from './pages/tools/GlycemiaConverter';
import BloodPressureChecker from './pages/tools/BloodPressureChecker';
import HydrationCalculator from './pages/tools/HydrationCalculator';
import CalorieCalculator from './pages/tools/CalorieCalculator';
import BodyFatCalculator from './pages/tools/BodyFatCalculator';
import GrowthTracker from './pages/tools/GrowthTracker';
import DehydrationCalculator from './pages/tools/DehydrationCalculator';
import FeverConverter from './pages/tools/FeverConverter';
import CreatinineClearance from './pages/tools/CreatinineClearance';
import SmokingSavingsCalculator from './pages/tools/SmokingSavingsCalculator';
import GestationalWeightGain from './pages/tools/GestationalWeightGain';
import CalciumVitaminD from './pages/tools/CalciumVitaminD';
import PrenuptialCertificate from './pages/tools/PrenuptialCertificate';
import BloodPanelConverter from './pages/tools/BloodPanelConverter';
import RamadanDiabetesRisk from './pages/tools/RamadanDiabetesRisk';
import SleepCycleCalculator from './pages/tools/SleepCycleCalculator';
import ColorPerceptionCheck from './pages/tools/ColorPerceptionCheck';
import StressSelfCheck from './pages/tools/StressSelfCheck';
import AntibioticPlanner from './pages/tools/AntibioticPlanner';
import CnasReimbursementSimulator from './pages/tools/CnasReimbursementSimulator';
import MaternityLeaveCalculator from './pages/tools/MaternityLeaveCalculator';
import DrugDuplicateChecker from './pages/tools/DrugDuplicateChecker';
import VerifyEmail from "./pages/VerifyEmail";
import ActiverProfil from "./pages/ActiverProfil";
import ClinicRegister from "./pages/ClinicRegister";
import AdminClinics from "./pages/AdminClinics";
import ClinicDashboard from "./pages/ClinicDashboard";
import ClinicProDashboard from "./pages/ClinicProDashboard";
import HopitalDashboard from "./pages/HopitalDashboard";
import HopitalInscription from "./pages/HopitalInscription";
import LaboRegister from "./pages/LaboRegister";
import LaboDashboard from "./pages/LaboDashboard";
import PharmacyRegister from "./pages/PharmacyRegister";
import PharmacyDashboard from "./pages/PharmacyDashboard";
import Pharmacies from "./pages/Pharmacies";
import PharmacyProfile from "./pages/PharmacyProfile";
import ClinicProfile from "./pages/ClinicProfile";
import ArticleDetail from "./pages/ArticleDetail";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail"; // ✅ NOUVEAU
import NotFound from "./pages/NotFound";
import MentionsLegales from "./pages/MentionsLegales";
import Confidentialite from "./pages/Confidentialite";
import DoctorsByWilaya from "./pages/DoctorsByWilaya";
import VerifyPrescription from "./pages/VerifyPrescription";
import PatientPortalLab from "./pages/PatientPortalLab";
import Laboratoires from "./pages/Laboratoires";
import LeaveReviewLab from "./pages/LeaveReviewLab";
import IncendiesSoutien from "./pages/IncendiesSoutien";
import TabbibAI from "./components/TabbibAI";
import SolidarityBanner from "./components/SolidarityBanner";

import "./index.css";

const AppRouter = () => {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/specialite/:specialtySlug/:wilayaSlug" element={<SpecialtyWilaya />} />
      <Route path="/doctor/:doctorId" element={<DoctorProfile />} />
      <Route path="/medecin/:slug" element={<DoctorProfile />} />
      <Route path="/doctor/register" element={<DoctorRegister />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/hopital/inscription" element={<HopitalInscription />} />
      <Route path="/laboratoire/inscription" element={<LaboRegister />} />
      <Route path="/pharmacie/inscription" element={<PharmacyRegister />} />
      <Route path="/activer-profil" element={<ActiverProfil />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/partenariat" element={<Partenariat />} />
      <Route path="/pharmacie" element={<PharmacyLanding />} />
      <Route path="/outils-sante" element={<HealthToolsHub />} />
      <Route path="/outils-sante/posologie-enfant" element={<PediatricDosage />} />
      <Route path="/outils-sante/imc" element={<BmiCalculator />} />
      <Route path="/outils-sante/ovulation" element={<OvulationCalculator />} />
      <Route path="/outils-sante/grossesse" element={<PregnancyTracker />} />
      <Route path="/outils-sante/date-accouchement" element={<PregnancyTracker />} />
      <Route path="/outils-sante/vaccination" element={<VaccinationCalendar />} />
      <Route path="/outils-sante/risque-cardiovasculaire" element={<CardiovascularRisk />} />
      <Route path="/outils-sante/triage-urgence" element={<EmergencyTriage />} />
      <Route path="/outils-sante/glycemie" element={<GlycemiaConverter />} />
      <Route path="/outils-sante/tension-arterielle" element={<BloodPressureChecker />} />
      <Route path="/outils-sante/hydratation" element={<HydrationCalculator />} />
      <Route path="/outils-sante/calories" element={<CalorieCalculator />} />
      <Route path="/outils-sante/masse-grasse" element={<BodyFatCalculator />} />
      <Route path="/outils-sante/croissance" element={<GrowthTracker />} />
      <Route path="/outils-sante/deshydratation" element={<DehydrationCalculator />} />
      <Route path="/outils-sante/fievre" element={<FeverConverter />} />
      <Route path="/outils-sante/clairance-creatinine" element={<CreatinineClearance />} />
      <Route path="/outils-sante/arret-tabac" element={<SmokingSavingsCalculator />} />
      <Route path="/outils-sante/prise-poids-grossesse" element={<GestationalWeightGain />} />
      <Route path="/outils-sante/calcium-vitamine-d" element={<CalciumVitaminD />} />
      <Route path="/outils-sante/bilan-prenuptial" element={<PrenuptialCertificate />} />
      <Route path="/outils-sante/bilan-sanguin" element={<BloodPanelConverter />} />
      <Route path="/outils-sante/ramadan-diabete" element={<RamadanDiabetesRisk />} />
      <Route path="/outils-sante/cycles-sommeil" element={<SleepCycleCalculator />} />
      <Route path="/outils-sante/perception-couleurs" element={<ColorPerceptionCheck />} />
      <Route path="/outils-sante/stress" element={<StressSelfCheck />} />
      <Route path="/outils-sante/planning-antibiotiques" element={<AntibioticPlanner />} />
      <Route path="/outils-sante/simulateur-cnas" element={<CnasReimbursementSimulator />} />
      <Route path="/outils-sante/conge-maternite" element={<MaternityLeaveCalculator />} />
      <Route path="/outils-sante/doublons-medicaments" element={<DrugDuplicateChecker />} />
      <Route path="/clinic/register" element={<ClinicRegister />} />
      <Route path="/admin/clinics" element={<AdminClinics />} />
      <Route path="/clinic/dashboard" element={<ClinicDashboard />} />
      <Route path="/clinic/pro" element={<ClinicProDashboard />} />
      <Route path="/hopital" element={<HopitalDashboard />} />
      <Route path="/laboratoire" element={<LaboDashboard />} />
      <Route path="/officine" element={<PharmacyDashboard />} />
      <Route path="/pharmacies" element={<Pharmacies />} />
      <Route path="/pharmacie/:slug" element={<PharmacyProfile />} />
      <Route path="/clinic/:slug" element={<ClinicProfile />} />
      <Route path="/article/:articleId" element={<ArticleDetail />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogDetail />} />
      <Route path="/verify/ord/:token" element={<VerifyPrescription />} />
      <Route path="/portail-patient/:token" element={<PatientPortalLab />} />
      <Route path="/laboratoires" element={<Laboratoires />} />
      <Route path="/avis-labo/:token" element={<LeaveReviewLab />} />
      <Route path="/incendies-2026" element={<IncendiesSoutien />} />

      {/* Patient */}
      <Route
        path="/patient/dashboard"
        element={
          <ProtectedRoute requiredRole="patient">
            <PatientDashboard />
          </ProtectedRoute>
        }
      />

      {/* Doctor */}
      <Route
        path="/doctor/dashboard"
        element={
          <ProtectedRoute requiredRole="doctor">
            <DoctorDashboard />
          </ProtectedRoute>
        }
      />

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Authenticated */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <Favorites />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/medecins/:wilaya/:specialty" element={<DoctorsByWilaya />} />
      <Route path="*" element={<NotFound />} />

    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <SolidarityBanner />
          <AppRouter />
          <Toaster position="top-right" richColors />
          <TabbibAI />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;